import type { Http } from '../../http'
import type { IUrls } from './urls'
import type { IRedirectData } from './types'
import { E_RedirectType } from './types'
import { TokenService } from '../../services/TokenService'
import _ from 'lodash'

interface ICacheValue {
    key: string
    value: any
}

const StorageKey = 'COMMON-WIDGET-REDIRECT-CONTROL'

export class RedirectControlService {
    private tokenService: TokenService

    constructor(
        private http: Http,
        private urls: IUrls,
        private clientId: string
    ) {
        this.tokenService = new TokenService()
    }

    /**
     * 获取是否发送请求的key
     */
    getCheckUpdateKey() {
        return this.tokenService?.session
    }

    getCacheValue() {
        const value = localStorage.getItem(StorageKey)
        if (!value) {
            return {}
        }
        try {
            const data = JSON.parse(value) as ICacheValue
            if (_.isObject(data)) {
                if (data.key === this.getCheckUpdateKey()) {
                    return data
                }
            }
        } catch (e) {
            return {}
        }
        return {}
    }

    storeCacheValue(data: boolean) {
        try {
            const storageData: ICacheValue = {
                key: this.getCheckUpdateKey(),
                value: data,
            }
            localStorage.setItem(StorageKey, JSON.stringify(storageData))
        } catch (e) {
            // ignore
        }
    }

    /**
     * 检查是否需要进行重定向
     */
    async checkRedirectRequired(): Promise<IRedirectData | null> {

        const cacheObj = this.getCacheValue() as ICacheValue
        const checkRedirectUpdateValue = cacheObj?.value

        if (checkRedirectUpdateValue !== undefined && !checkRedirectUpdateValue) {
            return Promise.resolve(null)
        }

        // 如果用户未登录，不进行重定向检查
        if (!this.tokenService.isTokenValid || this.tokenService.isAnonymous) {
            return null
        }

        try {
            // 检查MFA认证状态
            const mfaRequired = await this.checkMFARequired()
            if (mfaRequired) {
                return {
                    type: E_RedirectType.MFA_AUTH,
                    mfaUrl: mfaRequired.redirectUrl,
                    reason: mfaRequired.reason || 'MFA验证required',
                }
            }

            // 后续可以添加其他类型的重定向检查
            
            return null
        } catch (error) {
            console.error('检查重定向状态失败:', error)
            return null
        }
    }

    /**
     * 检查MFA认证状态
     */
    protected async checkMFARequired(): Promise<{ redirectUrl: string; reason?: string } | null> {
        try {
            const response: any = await this.http.request(this.urls.checkMFAStatus, {
                method: 'get',
                withCredentials: true,
            })
            
            // 200状态码表示不需要MFA，直接返回null
            if (response.status === 200) {
                return null
            }
            
            return null
        } catch (error: any) {
            // 检查是否是403错误，且错误码为MFA相关
            if (error.response?.status === 403) {
                const errorData = error.response.data
                if (errorData?.numeric_error_code === 32004) {
                    // 构建MFA跳转URL，这里需要根据实际情况调整
                    const mfaUrl = this.buildMFARedirectUrl()
                    return {
                        redirectUrl: mfaUrl,
                        reason: errorData.error_message || 'MFA验证required',
                    }
                }
            }
            
            // 其他错误不强制跳转
            console.warn('MFA状态检查失败:', error)
            return null
        }
    }

    /**
     * 构建MFA跳转URL
     */
    private buildMFARedirectUrl(): string {
        // 这里需要根据实际的MFA页面URL来构建
        // 可以从配置中获取或者使用固定的URL模式
        const baseUrl = this.urls.accountUrl
        const redirectUrl = encodeURIComponent(window.location.href)
        return `${baseUrl}/user-settings/#/security-settings?redirect=${redirectUrl}`
    }

    /**
     * 跳转到MFA认证页面
     */
    redirectToMFA(mfaUrl: string) {
        // 记录重定向信息
        this.setRedirectInfo({
            type: E_RedirectType.MFA_AUTH,
            timestamp: Date.now(),
            originalUrl: window.location.href,
        })

        // 执行跳转
        window.location.href = mfaUrl
    }

    /**
     * 获取重定向信息（用于回跳后的处理）
     */
    getRedirectInfo(): any {
        try {
            const info = localStorage.getItem(StorageKey)
            return info ? JSON.parse(info) : null
        } catch (error) {
            console.error('获取重定向信息失败:', error)
            return null
        }
    }

    /**
     * 设置重定向信息
     */
    private setRedirectInfo(info: any) {
        try {
            localStorage.setItem(StorageKey, JSON.stringify(info))
        } catch (error) {
            console.error('设置重定向信息失败:', error)
        }
    }

    /**
     * 清除重定向信息
     */
    clearRedirectInfo() {
        localStorage.removeItem(StorageKey)
    }

    /**
     * 处理MFA认证完成后的回跳
     */
    handleMFACallback() {
        const redirectInfo = this.getRedirectInfo()
        if (redirectInfo && redirectInfo.type === E_RedirectType.MFA_AUTH) {
            this.clearRedirectInfo()
            // 可以在这里添加回跳后的处理逻辑
            console.log('MFA认证完成，原始URL:', redirectInfo.originalUrl)
        }
    }
}

/**
 * Mock服务用于测试
 */
export class RedirectControlServiceMock extends RedirectControlService {
    async checkRedirectRequired(): Promise<IRedirectData | null> {
        // Mock模式下模拟需要MFA认证
        if (Math.random() > 0.7) { // 30%概率需要MFA
            return {
                type: E_RedirectType.MFA_AUTH,
                mfaUrl: `https://example.com/mfa/binding?redirect=${encodeURIComponent(window.location.href)}`,
                reason: 'Mock: Company requires MFA binding, please bind MFA device first.',
            }
        }
        return null
    }

    /**
     * 重写checkMFARequired方法来模拟403错误
     */
    protected async checkMFARequired(): Promise<{ redirectUrl: string; reason?: string } | null> {
        // Mock 403错误响应
        if (Math.random() > 0.7) { // 30%概率触发403
            const mockError = {
                response: {
                    status: 403,
                    data: {
                        status_code: 403,
                        numeric_error_code: 32004,
                        error_code: 'errors.com.patsnap.mfa.binding.required',
                        error_message: 'Company requires MFA binding, please bind MFA device first.',
                        message_vars: [],
                        originating_service: 's-passport-service',
                        timestamp: new Date().toISOString(),
                    },
                },
            }
            throw mockError
        }
        
        // Mock 200正常响应
        return null
    }

    redirectToMFA(mfaUrl: string) {
        // Mock模式下不实际跳转，只打印日志
        console.log('Mock MFA跳转:', mfaUrl)
        alert(`Mock MFA跳转到: ${mfaUrl}`)
    }
} 